import { useEffect, useMemo, useState } from 'react'

import { getPrice } from '@fourthwall/utils/dist/common'

import { useSubscriptionPlansQuery } from '../../../queries/useSubscriptionPlansQuery'
import type { Plan } from '../AddSubscriptionModal.types'

export const usePlans = (initialPlanId?: string) => {
  const [selectedPlanId, setSelectedPlanId] = useState(initialPlanId)
  const { subscriptionPlansQuery } = useSubscriptionPlansQuery()

  const proPlans = useMemo(() => {
    return subscriptionPlansQuery.data?.filter((plan) => plan.pro)
  }, [subscriptionPlansQuery.data])

  const testPlan = useMemo(() => {
    return proPlans?.find((plan) => plan.recurringPeriod === 'FIFTEEN_MINUTES')
  }, [proPlans])

  const monthlyPlan = useMemo(() => {
    return proPlans?.find((plan) => plan.recurringPeriod === 'MONTHLY')
  }, [proPlans])

  const yearlyPlan = useMemo(() => {
    return proPlans?.find((plan) => plan.recurringPeriod === 'YEARLY')
  }, [proPlans])

  const plans: Plan[] = useMemo(() => {
    if (!monthlyPlan && !yearlyPlan) return []

    const result = []

    // NOTE: temporary - remove after testing period
    if (testPlan) {
      const monthlyPriceValue = testPlan.price.value * 4 * 24 * 30

      result.push({
        id: testPlan.id,
        label: `15 minutes (${getPrice(testPlan.price.value, { currency: testPlan.price.currency })})`,
        monthlyPrice: {
          value: monthlyPriceValue,
          currency: testPlan.price.currency,
        },
        totalPrice: testPlan.price,
      })
    }

    if (monthlyPlan) {
      result.push({
        id: monthlyPlan.id,
        label: 'Monthly',
        monthlyPrice: monthlyPlan.price,
        totalPrice: monthlyPlan.price,
        analytics: 't2_monthly',
      })
    }

    if (yearlyPlan) {
      const monthlyPriceValue = yearlyPlan.price.value / 12
      const savingsValue = monthlyPlan ? (monthlyPlan.price.value - monthlyPriceValue) * 12 : 0

      result.push({
        id: yearlyPlan.id,
        label: `Yearly (${getPrice(yearlyPlan.price.value, { currency: yearlyPlan.price.currency })} per year)`,
        monthlyPrice: {
          value: monthlyPriceValue,
          currency: yearlyPlan.price.currency,
        },
        totalPrice: yearlyPlan.price,
        savings: monthlyPlan
          ? { value: savingsValue, currency: yearlyPlan.price.currency }
          : undefined,
        analytics: 't2_annual',
      })
    }

    return result
  }, [monthlyPlan, testPlan, yearlyPlan])

  const selectedPlan = useMemo(
    () => plans.find((plan) => plan.id === selectedPlanId),
    [plans, selectedPlanId],
  )

  useEffect(() => {
    const newInitialPlanId = initialPlanId || monthlyPlan?.id || yearlyPlan?.id

    if (newInitialPlanId) {
      setSelectedPlanId(newInitialPlanId)
    }
  }, [monthlyPlan, yearlyPlan, initialPlanId])

  return { plans, selectedPlan, setSelectedPlan: setSelectedPlanId }
}
